import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Button,
  SimpleGrid,
  Box,
  Flex,
  Text,
  Divider,
  Link,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaShieldAlt } from "react-icons/fa";
import { ivApi, isOk, BREAKDOWN_REFUSALS, SETTINGS_PATH } from "./insuranceVerification";

/**
 * VerifyEligibilityModal — the confirm sheet (SS-3944).
 *
 * Prefilled from SS-3939's prefill (patient, subscriber or dependent, member id, group,
 * insurer, dentist, date of service). Every field is editable; the ones a check cannot be
 * sent without are highlighted while empty. Shows the cost in insurance credits (SS-4515: their own
 * meter, not AI credits): Viva keeps no
 * balance — the API sends the usage to Stripe through the worker's meter event, as Ezra's
 * calls and texts do, and it is billed with the practice's subscription.
 */

const day = (d) => (d ? String(d).slice(0, 10) : "");

const formFromPrefill = (p = {}) => {
  const sub = p.subscriber || {};
  const dep = p.dependent || null;
  return {
    relationship: dep ? "dependent" : "self",
    patientFirst: (dep || sub).firstName || "",
    patientLast: (dep || sub).lastName || "",
    patientDob: day((dep || sub).dateOfBirth),
    subFirst: dep ? sub.firstName || "" : "",
    subLast: dep ? sub.lastName || "" : "",
    subDob: dep ? day(sub.dateOfBirth) : "",
    zipCode: sub.zipCode || "",
    memberId: sub.id || p.member_id || "",
    groupId: sub.groupId || p.group_number || "",
    payerId: p.payerId || "",
    providerId: p.providerId || "",
    serviceDate: day(p.serviceDate),
  };
};

// the fields the API cannot send a check without (SS-3939 missingFields), by form field
export const missingFormFields = (v, { providers = [] } = {}) => {
  const missing = new Set();
  const need = (k) => { if (!String(v[k] || "").trim()) missing.add(k); };
  ["patientFirst", "patientLast", "patientDob", "memberId", "payerId", "serviceDate", "providerId"].forEach(need);
  if (v.relationship === "dependent") ["subFirst", "subLast", "subDob"].forEach(need);
  const provider = providers.find((p) => String(p.provider_id) === String(v.providerId));
  if (v.providerId && !provider?.npi) missing.add("providerId");
  return missing;
};

// the API's missing field names → the form's
const API_FIELD = {
  "subscriber.firstName": (dep) => (dep ? "subFirst" : "patientFirst"),
  "subscriber.lastName": (dep) => (dep ? "subLast" : "patientLast"),
  "subscriber.dateOfBirth": (dep) => (dep ? "subDob" : "patientDob"),
  "subscriber.id": () => "memberId",
  "dependent.firstName": () => "patientFirst",
  "dependent.lastName": () => "patientLast",
  "dependent.dateOfBirth": () => "patientDob",
  payerId: () => "payerId",
  "provider.nationalProviderId": () => "providerId",
  "provider.name": () => "providerId",
  serviceDate: () => "serviceDate",
};

export const bodyFromForm = (v, type) => {
  const dependent = v.relationship === "dependent";
  const subscriber = dependent
    ? { firstName: v.subFirst, lastName: v.subLast, dateOfBirth: v.subDob }
    : { firstName: v.patientFirst, lastName: v.patientLast, dateOfBirth: v.patientDob };
  return {
    type,
    subscriber: {
      ...subscriber,
      id: v.memberId.trim(),
      ...(v.groupId.trim() ? { groupId: v.groupId.trim() } : {}),
      ...(v.zipCode ? { zipCode: v.zipCode } : {}),
    },
    ...(dependent ? { dependent: { firstName: v.patientFirst, lastName: v.patientLast, dateOfBirth: v.patientDob } } : {}),
    payerId: v.payerId,
    providerId: v.providerId,
    serviceDate: v.serviceDate,
  };
};

export default function VerifyEligibilityModal({
  isOpen,
  onClose,
  patientId,
  type = "eligibility",
  prefill,
  settings,
  payer,
  onSubmitted,
}) {
  const [values, setValues] = useState(() => formFromPrefill(prefill));
  const chosenPayer = useRef("");
  chosenPayer.current = values.payerId;
  const [payerOptions, setPayerOptions] = useState([]);
  const [payerSearch, setPayerSearch] = useState("");
  const [apiMissing, setApiMissing] = useState(new Set());
  const [officeMissing, setOfficeMissing] = useState(false);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);

  const labelColor = useColorModeValue("#11131B", "#F9F9FB");
  const subColor = useColorModeValue("#64728C", "#A9B1C2");
  const sectionBg = useColorModeValue("#F9F9FB", "#2A2D3A");
  const primary = "#154043";

  useEffect(() => {
    if (!isOpen) return;
    setValues(formFromPrefill(prefill));
    setApiMissing(new Set());
    setOfficeMissing(false);
    setError("");
    const initial = [
      ...(payer?.commonPayerId ? [{ commonPayerId: payer.commonPayerId, name: payer.name }] : []),
      ...(prefill?.payer_suggestions || []),
    ];
    setPayerOptions(initial.filter((p, i) => initial.findIndex((x) => x.commonPayerId === p.commonPayerId) === i));
  }, [isOpen, prefill, payer]);

  const providers = useMemo(() => {
    const list = settings?.provider_npis || [];
    const known = list.some((p) => String(p.provider_id) === String(values.providerId));
    return known || !values.providerId ? list : [...list, { provider_id: values.providerId, first_name: "", last_name: "", npi: "" }];
  }, [settings, values.providerId]);

  const localMissing = missingFormFields(values, { providers });
  const missing = new Set([...localMissing, ...[...apiMissing].filter((k) => !String(values[k] || "").trim())]);
  const cost = settings?.credits?.[type] ?? (type === "breakdown" ? 8 : 2);

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const searchPayers = async () => {
    const r = await ivApi.searchPayers(payerSearch);
    if (!isOk(r)) return;
    // the insurer already chosen stays chosen (and visible) whatever the search returns
    setPayerOptions((cur) => {
      // one option per payer id (its value): an id can stand for several network names, and the patient's own
      // insurer keeps the name the card gives it, the chosen one the name it was chosen by (SS-4455)
      const chosen = cur.find((p) => p.commonPayerId === chosenPayer.current);
      const names = new Map([...(payer?.commonPayerId ? [[payer.commonPayerId, payer.name]] : []), ...(chosen ? [[chosen.commonPayerId, chosen.name]] : [])]);
      const seen = new Set();
      const list = (r.data || []).filter((p) => !seen.has(p.commonPayerId) && seen.add(p.commonPayerId))
        .map((p) => (names.has(p.commonPayerId) ? { ...p, name: names.get(p.commonPayerId) } : p));
      return chosen && !list.some((p) => p.commonPayerId === chosen.commonPayerId) ? [chosen, ...list] : list;
    });
  };

  const run = async () => {
    setRunning(true);
    setError("");
    try {
      // a payer staff chose for the chart's insurer name is remembered for next time
      if (values.payerId && prefill?.carrier_name && values.payerId !== prefill?.payerId) {
        await ivApi.putPayerMap([{ carrier_name: prefill.carrier_name, commonPayerId: values.payerId }]);
      }
      const r = await ivApi.createVerification(patientId, bodyFromForm(values, type));
      if (isOk(r)) {
        onSubmitted && onSubmitted(r.data);
        return;
      }
      if (r?.code === "missing_fields") {
        const dep = values.relationship === "dependent";
        const fields = new Set();
        (r.missing || []).forEach((m) => {
          if (API_FIELD[m]) fields.add(API_FIELD[m](dep));
          if (m.startsWith("office.")) setOfficeMissing(true);
        });
        setApiMissing(fields);
        setError("Some required details are missing.");
      } else if (BREAKDOWN_REFUSALS[r?.code]) {
        setError(BREAKDOWN_REFUSALS[r.code]);
      } else {
        setError(r?.msg || "The check could not be sent. Try again.");
      }
    } finally {
      setRunning(false);
    }
  };

  const field = (k, label, props = {}) => (
    <FormControl isInvalid={missing.has(k)} data-testid={`iv-field-${k}`}>
      <FormLabel fontSize="13px" mb="1">{label}</FormLabel>
      <Input size="sm" borderRadius="8px" value={values[k]} onChange={set(k)} {...props} />
      <FormErrorMessage fontSize="12px">Missing</FormErrorMessage>
    </FormControl>
  );

  const sectionTitle = (t) => (
    <Text fontSize="12px" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" color={subColor} mb="2">{t}</Text>
  );

  const isDependent = values.relationship === "dependent";
  const title = type === "breakdown" ? "Full breakdown" : "Check eligibility";

  return (
    <Modal isOpen={isOpen} onClose={running ? () => {} : onClose} size={{ base: "full", md: "lg" }} scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={{ base: 0, md: "16px" }} data-testid="iv-confirm-sheet">
        <ModalHeader pb="1">
          <Flex align="center" gap="8px">
            <Box color={primary}><FaShieldAlt /></Box>
            <Text fontSize="18px" fontWeight="700" color={labelColor}>{title}</Text>
          </Flex>
          <Text fontSize="13px" fontWeight="400" color={subColor} mt="1">
            {"Pre-filled from the patient's chart. Confirm and complete anything highlighted, then run the check."}
          </Text>
        </ModalHeader>
        {!running && <ModalCloseButton />}

        <ModalBody>
          {sectionTitle("Patient")}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb="2">
            {field("patientFirst", "First name")}
            {field("patientLast", "Last name")}
            {field("patientDob", "Date of birth", { type: "date" })}
            <FormControl>
              <FormLabel fontSize="13px" mb="1">Patient is</FormLabel>
              <Select size="sm" borderRadius="8px" value={values.relationship} onChange={set("relationship")} data-testid="iv-relationship">
                <option value="self">The subscriber</option>
                <option value="dependent">A dependent</option>
              </Select>
            </FormControl>
          </SimpleGrid>
          {isDependent && (
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb="2">
              {field("subFirst", "Subscriber first name")}
              {field("subLast", "Subscriber last name")}
              {field("subDob", "Subscriber date of birth", { type: "date" })}
            </SimpleGrid>
          )}

          <Divider my="3" />
          {sectionTitle("Insurance")}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb="2">
            {field("memberId", "Member ID")}
            {field("groupId", "Group number")}
          </SimpleGrid>
          <FormControl isInvalid={missing.has("payerId")} data-testid="iv-field-payerId" mb="2">
            <FormLabel fontSize="13px" mb="1">Insurer</FormLabel>
            <Select size="sm" borderRadius="8px" value={values.payerId} onChange={set("payerId")} placeholder="Choose the insurer">
              {payerOptions.map((p) => <option key={p.commonPayerId} value={p.commonPayerId}>{p.name}</option>)}
            </Select>
            <FormErrorMessage fontSize="12px">Missing</FormErrorMessage>
            {prefill?.carrier_name && (
              <Text fontSize="12px" color={subColor} mt="1">Chart says: {prefill.carrier_name}</Text>
            )}
            <Flex gap="2" mt="2">
              <Input size="sm" borderRadius="8px" placeholder="Search insurers" value={payerSearch}
                onChange={(e) => setPayerSearch(e.target.value)} data-testid="iv-payer-search" />
              <Button size="sm" variant="outline" onClick={searchPayers}>Search</Button>
            </Flex>
          </FormControl>

          <Divider my="3" />
          {sectionTitle("Appointment")}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <FormControl isInvalid={missing.has("providerId")} data-testid="iv-field-providerId">
              <FormLabel fontSize="13px" mb="1">Dentist</FormLabel>
              <Select size="sm" borderRadius="8px" value={values.providerId} onChange={set("providerId")} placeholder="Choose the dentist">
                {providers.map((p) => (
                  <option key={p.provider_id} value={p.provider_id}>
                    {[p.first_name, p.last_name].filter(Boolean).join(" ") || `Provider ${p.provider_id}`}{p.npi ? ` · NPI ${p.npi}` : " · no NPI"}
                  </option>
                ))}
              </Select>
              <FormErrorMessage fontSize="12px">Missing (a dentist with an NPI)</FormErrorMessage>
            </FormControl>
            {field("serviceDate", "Date of service", { type: "date" })}
          </SimpleGrid>

          {officeMissing && (
            <Alert status="warning" mt="3" borderRadius="8px" fontSize="13px">
              <AlertIcon />
              <Text>The practice details registered with insurers are incomplete. <Link href={SETTINGS_PATH} color="#8E4997" fontWeight="600">Open settings</Link></Text>
            </Alert>
          )}

          <Box bg={sectionBg} borderRadius="8px" p="3" mt="4" data-testid="iv-cost">
            <Flex justify="space-between">
              <Text fontSize="13px" color={subColor}>Cost</Text>
              <Text fontSize="13px" fontWeight="700" color={labelColor}>{cost} credits</Text>
            </Flex>
            <Text fontSize="12px" color={subColor} mt="1" data-testid="iv-billing">
              Added to your practice&apos;s insurance verification usage and billed with your subscription.
            </Text>
          </Box>

          {error && (
            <Alert status="warning" mt="3" borderRadius="8px" fontSize="13px" data-testid="iv-error">
              <AlertIcon />{error}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter gap="3">
          {running && (
            <Flex align="center" gap="2" mr="auto">
              <Spinner size="sm" color={primary} />
              <Text fontSize="13px" color={subColor}>{type === "breakdown" ? "Sending the request…" : "Checking with the insurer…"}</Text>
            </Flex>
          )}
          <Button variant="ghost" size="sm" onClick={onClose} isDisabled={running}>Cancel</Button>
          <Button
            size="sm"
            bg={primary}
            color="#F9F9FB"
            _hover={{ bg: "#123536" }}
            borderRadius="8px"
            leftIcon={<FaShieldAlt size={13} />}
            isDisabled={running || missing.size > 0}
            onClick={run}
            data-testid="iv-run"
          >
            Run check · {cost} credits
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
